import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Button } from '@gtintel/signin/components/common/Button';
import { Input } from '@gtintel/signin/components/common/Input';
import { Loading } from '@gtintel/signin/components/common/Loading';
import { mfaService } from '@gtintel/signin/services/mfa.service';

const totpSchema = yup.object({
  code: yup
    .string()
    .required('Code is required')
    .matches(/^\d{6}$/, 'Code must be 6 digits'),
});

const smsSchema = yup.object({
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format'),
});

interface MFASetupProps {
  method: 'totp' | 'sms';
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const MFASetup: React.FC<MFASetupProps> = ({ method, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [setupComplete, setSetupComplete] = useState(false);

  const totpForm = useForm<{ code: string }>({
    resolver: yupResolver(totpSchema),
  });

  const smsForm = useForm<{ phoneNumber: string }>({
    resolver: yupResolver(smsSchema),
  });

  useEffect(() => {
    if (method === 'totp') {
      loadTOTPSetup();
    }
  }, [method]);

  const loadTOTPSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await mfaService.getTOTPSetup();
      if (response.success && response.qr_code) {
        setQrCodeUrl(response.qr_code);
        setSecretKey(response.secret_key || '');
        setBackupCodes(response.backup_codes || []);
      }
    } catch (err: any) {
      setError(err.error || 'Failed to load TOTP setup');
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPSubmit = async (data: { code: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await mfaService.verifyTOTPSetup({
        code: data.code,
        secret_key: secretKey,
      });
      if (response.success) {
        setSetupComplete(true);
        onSuccess?.();
      }
    } catch (err: any) {
      setError(err.error || 'Failed to verify TOTP setup');
    } finally {
      setLoading(false);
    }
  };

  const handleSMSSubmit = async (data: { phoneNumber: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await mfaService.setupSMS(data.phoneNumber);
      if (response.success) {
        setSetupComplete(true);
        onSuccess?.();
      }
    } catch (err: any) {
      setError(err.error || 'Failed to setup SMS MFA');
    } finally {
      setLoading(false);
    }
  };

  if (loading && method === 'totp' && !qrCodeUrl) {
    return <Loading size="lg" />;
  }

  if (setupComplete) {
    return (
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">
          {method === 'totp' ? 'Authenticator App' : 'SMS'} MFA Enabled
        </h3>
        <p className="text-sm text-gray-600">
          Your account is now protected with multi-factor authentication.
        </p>
        {backupCodes.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">Backup Codes</h4>
            <p className="text-xs text-yellow-700 mb-2">
              Save these codes in a secure place. Each can be used once if you lose access to your
              authenticator.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <code key={index} className="text-xs bg-yellow-100 p-1 rounded">
                  {code}
                </code>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Set Up {method === 'totp' ? 'Authenticator App' : 'SMS'} MFA
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {method === 'totp'
            ? 'Scan the QR code with your authenticator app and enter the verification code.'
            : 'Enter your phone number to receive verification codes via SMS.'}
        </p>
      </div>

      {method === 'totp' ? (
        <>
          {qrCodeUrl && (
            <div className="flex flex-col items-center space-y-4">
              <div className="p-4 bg-white border-2 border-gray-300 rounded-lg">
                <img src={qrCodeUrl} alt="TOTP QR Code" className="w-48 h-48" />
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Can't scan? Enter this code manually:</p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">{secretKey}</code>
              </div>
            </div>
          )}

          <form onSubmit={totpForm.handleSubmit(handleTOTPSubmit)} className="space-y-4">
            <Input
              {...totpForm.register('code')}
              type="text"
              label="Verification code"
              placeholder="000000"
              error={totpForm.formState.errors.code?.message}
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
            />

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div className="flex space-x-3">
              <Button type="submit" fullWidth loading={loading}>
                Verify & Enable
              </Button>
              <Button type="button" variant="secondary" fullWidth onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </>
      ) : (
        <form onSubmit={smsForm.handleSubmit(handleSMSSubmit)} className="space-y-4">
          <Input
            {...smsForm.register('phoneNumber')}
            type="tel"
            label="Phone number"
            placeholder="+1234567890"
            error={smsForm.formState.errors.phoneNumber?.message}
          />

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="flex space-x-3">
            <Button type="submit" fullWidth loading={loading}>
              Send Verification Code
            </Button>
            <Button type="button" variant="secondary" fullWidth onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
